import { type ForwardedRef, forwardRef, useEffect } from 'react';

import { type ListCountriesQuery, type ListCountriesQueryVariables, useListCountriesQuery } from '@/common/graphql';

import type { ComboboxItemWithFormatted, CommonProps, UseQueryReturn } from '../props';
import { SelectMultiple, type SelectMultipleProps } from '../SelectMultiple';
import { SelectSingle, type SelectSingleProps } from '../SelectSingle';

type Variables = Partial<Omit<ListCountriesQueryVariables, 'pageNumber' | 'pageSize' | 'keyword'>>;
type Data = ComboboxItemWithFormatted &
  Omit<ListCountriesQuery['listCountries']['items'][number], 'id' | 'name' | 'demonym'>;
type Props = CommonProps<Variables>;

const useQuery = (
  variables: Variables | undefined,
  onLoadingCompleted?: () => void,
): UseQueryReturn<ListCountriesQueryVariables, Data> => {
  const { data, ...rest } = useListCountriesQuery({
    variables: {
      pageNumber: null,
      pageSize: null,
      keyword: null,
      hasCompanies: variables?.hasCompanies ?? null,
      hasNationalities: variables?.hasNationalities ?? null,
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (onLoadingCompleted) onLoadingCompleted();
  }, [onLoadingCompleted]);

  return {
    data:
      data?.listCountries?.items?.map(it => ({
        value: it.id,
        label: `${it.demonym} (${it.name})`,
        labelFormatted: (
          <div className="flex flex-wrap items-center justify-between gap-1">
            <span>{it.demonym}</span>
            <span className="text-gray-7 text-2xs">{it.name}</span>
          </div>
        ),
        disabled: false,
      })) || [],
    ...rest,
  };
};

export const NationalitySelectSingle = forwardRef(
  (props: Props & SelectSingleProps<Data>, ref: ForwardedRef<HTMLDivElement>) => {
    const { data, loading } = useQuery(props.variables, props.onLoadingCompleted);
    return <SelectSingle ref={ref} {...props} data={data} loading={loading} />;
  },
);

export const NationalitySelectMultiple = (props: Props & SelectMultipleProps<Data>) => {
  const { data, loading } = useQuery(props.variables, props.onLoadingCompleted);
  return (
    <SelectMultiple
      multipleSelectedLabel={length => `${length} nationalities`}
      {...props}
      data={data}
      loading={loading}
    />
  );
};
